import React from 'react';
import { cn } from '../../utils/cn';

// Static class maps — Tailwind JIT requires complete class names in source
const weightClasses: Record<string, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  black: 'font-black',
};

const alignClasses: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

// --- Types & Variants ---

type HeadingVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type TextVariant = 'body' | 'body-sm' | 'caption' | 'tiny';
type LabelVariant = 'default' | 'subtle' | 'error' | 'success' | 'warning';
type FontWeight = 'normal' | 'medium' | 'semibold' | 'bold' | 'black';

interface BaseProps {
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
  color?: string; // Optional override, but prefer variant-based colors
}

// --- Heading Component ---

interface HeadingProps extends BaseProps {
  variant?: HeadingVariant;
  weight?: FontWeight;
}

const headingStyles: Record<HeadingVariant, string> = {
  h1: 'text-3xl sm:text-4xl leading-tight tracking-tight',
  h2: 'text-2xl sm:text-3xl leading-tight tracking-tight',
  h3: 'text-xl sm:text-2xl leading-snug tracking-tight',
  h4: 'text-lg sm:text-xl leading-snug font-bold',
  h5: 'text-base sm:text-lg leading-normal font-semibold',
  h6: 'text-sm sm:text-base leading-normal font-semibold uppercase tracking-wide',
};

export const Heading: React.FC<HeadingProps> = ({
  variant = 'h2',
  weight,
  align = 'left',
  color,
  className,
  children,
  ...props
}) => {
  const Component = variant;

  // Default weights per variant if not specified
  const defaultWeights: Record<HeadingVariant, FontWeight> = {
    h1: 'black',
    h2: 'bold',
    h3: 'bold',
    h4: 'bold',
    h5: 'semibold',
    h6: 'bold',
  };

  const finalWeight = weight || defaultWeights[variant];

  return (
    <Component
      className={cn(
        headingStyles[variant],
        weightClasses[finalWeight] || 'font-bold',
        alignClasses[align] || 'text-left',
        color || 'text-gray-900 dark:text-zinc-50',
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// --- Text Component ---

interface TextProps extends BaseProps {
  variant?: TextVariant;
  weight?: FontWeight;
  as?: 'p' | 'span' | 'div';
}

const textStyles: Record<TextVariant, string> = {
  body: 'text-base leading-relaxed',
  'body-sm': 'text-sm leading-relaxed',
  caption: 'text-xs leading-normal',
  tiny: 'text-[11px] leading-tight',
};

export const Text: React.FC<TextProps> = ({
  variant = 'body',
  weight = 'normal',
  align = 'left',
  color,
  as: Component = 'p',
  className,
  children,
  ...props
}) => {
  return (
    <Component
      className={cn(
        textStyles[variant],
        weightClasses[weight] || 'font-normal',
        alignClasses[align] || 'text-left',
        color || 'text-gray-600 dark:text-zinc-300',
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// --- Label Component ---
// Useful for small badges, form labels, or metadata tags

interface LabelProps extends BaseProps {
  variant?: LabelVariant;
  required?: boolean;
  htmlFor?: string;
}

const labelVariants: Record<LabelVariant, string> = {
  default: 'text-gray-700 dark:text-zinc-200',
  subtle: 'text-gray-500 dark:text-zinc-400',
  error: 'text-red-500 dark:text-red-400',
  success: 'text-green-500 dark:text-green-400',
  warning: 'text-amber-500 dark:text-amber-400',
};

export const Label: React.FC<LabelProps> = ({
  variant = 'default',
  required,
  align = 'left',
  className,
  children,
  htmlFor,
  ...props
}) => {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'block text-sm font-medium mb-1',
        labelVariants[variant],
        alignClasses[align] || 'text-left',
        className,
      )}
      {...props}
    >
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
};

// --- Badge Component (New) ---
// Since we are standardizing, let's add a consistent Badge here too

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'neutral' | 'outline';
  size?: 'sm' | 'md';
  className?: string;
}

const badgeVariants = {
  default:
    'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border-primary-200 dark:border-primary-800',
  success:
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800',
  warning:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  error:
    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800',
  neutral:
    'bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-zinc-300 border-gray-200 dark:border-zinc-700',
  outline: 'bg-transparent text-gray-600 dark:text-zinc-400 border-gray-300 dark:border-zinc-600',
};

const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'md',
  children,
  className,
}) => {
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center font-semibold rounded-full border',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-0.5 text-xs',
        badgeVariants[variant],
        className,
      )}
    >
      {children}
    </span>
  );
};

export const Typography = {
  Heading,
  Text,
  Label,
  Badge,
};
